/**
 * Place → 임패커스 OS 텔레메트리 전송기.
 *
 * `<TelemetryProvider>` 가 보내는 기본 이벤트(session_start / page_view / page_exit /
 * heartbeat / 첫 로드 page_load_timing) 외에, 서비스별 지표 대시보드에 필요한
 * 사용 속도(web_vital, query_timing)와 에러(error)를 같은 세션으로 보낸다.
 *
 * Provider 는 임의 이벤트를 보내는 공개 API 가 없어서 `@impakers/debug/telemetry` 의
 * 저수준 함수로 직접 만든다. 세션/익명 ID 는 기본 저장소 키를 그대로 써야 한다 —
 * 익명 토큰이 그 두 값에 묶여 발급되므로 다르면 OS 가 400 으로 거절한다.
 *
 * 이벤트 계약(속성 키)은 docs/design-docs/telemetry-events.md 참고.
 */
import {
  collectTelemetryEnvironment,
  computeDeviceFingerprint,
  createTelemetryEvent,
  getOrCreateTelemetryAnonymousId,
  getOrCreateTelemetrySessionId,
  getOrFetchTelemetryAnonymousToken,
  sendTelemetryEvent,
  type DeploymentEnvironment,
  type TelemetryAttributes,
} from '@impakers/debug/telemetry'
import {
  isIgnorableTelemetryError,
  normalizeStackFrame,
  TelemetryErrorThrottle,
  toCapturedError,
} from '@/lib/telemetry/error-normalize'
import { toRoutePattern } from '@/lib/telemetry/query-timing'

export const PLACE_TELEMETRY_SERVICE_ID = process.env.NEXT_PUBLIC_OS_TELEMETRY_SERVICE_ID || undefined
export const PLACE_TELEMETRY_SERVICE_NAME = 'Place'

/**
 * Provider 에 넘기는 environment 는 **반드시 모듈 상수**여야 한다.
 * 렌더마다 새 객체를 넘기면 Provider 내부 effect 가 매번 다시 돌아, 첫 로드의
 * page_load_timing(LCP 포함)이 라우트 전환마다 새 경로 이름으로 재전송된다.
 */
export const PLACE_TELEMETRY_ENVIRONMENT = { app: 'place' }

const DEPLOYMENT_ENVIRONMENTS: readonly string[] = ['local', 'dev', 'staging', 'production']

function isDeploymentEnvironment(value: string | undefined): value is DeploymentEnvironment {
  return value !== undefined && DEPLOYMENT_ENVIRONMENTS.includes(value)
}

/**
 * 1. NEXT_PUBLIC_DEPLOYMENT_ENV (명시 오버라이드)
 * 2. NEXT_PUBLIC_VERCEL_ENV — DEV 는 `vercel deploy`(프리뷰) 빌드라 NODE_ENV 가 production 이다.
 *    이걸 안 보면 DEV 에서 난 에러가 production 에러 그룹에 섞인다. preview → staging 은 SDK 규칙과 같다.
 * 3. NODE_ENV — 로컬 `next dev` 는 local
 */
function resolveDeploymentEnvironment(): DeploymentEnvironment {
  const explicit = process.env.NEXT_PUBLIC_DEPLOYMENT_ENV
  if (isDeploymentEnvironment(explicit)) return explicit
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV
  if (vercelEnv === 'preview') return 'staging'
  if (vercelEnv === 'production') return 'production'
  return process.env.NODE_ENV === 'production' ? 'production' : 'local'
}

export const PLACE_TELEMETRY_DEPLOYMENT_ENV = resolveDeploymentEnvironment()

let telemetryUserId: string | null = null
let telemetryDeploymentEnvironment: DeploymentEnvironment = PLACE_TELEMETRY_DEPLOYMENT_ENV
let deviceFingerprint: string | null | undefined

/** 로그인 사용자 UUID. 이름·이메일은 절대 보내지 않는다. */
export function setPlaceTelemetryUserId(userId: string | null | undefined): void {
  telemetryUserId = userId || null
}

/**
 * 서버(layout)가 정한 배포 환경. 브라우저는 서버 전용 `VERCEL_ENV` 를 못 읽으므로
 * `RuntimeInstrumentation` 이 받은 값을 넘겨 Provider 기본 이벤트와 맞춘다.
 */
export function setPlaceTelemetryDeploymentEnvironment(environment: DeploymentEnvironment): void {
  telemetryDeploymentEnvironment = environment
}

function getDeviceFingerprint(): string | null {
  if (deviceFingerprint === undefined) deviceFingerprint = computeDeviceFingerprint()
  return deviceFingerprint
}

export interface EmitPlaceTelemetryOptions {
  /** 이벤트를 귀속시킬 경로. 생략하면 현재 경로. */
  pathname?: string
}

/**
 * 이벤트 1건을 보낸다. 실패는 조용히 버린다 — 텔레메트리가 앱 동작을 막으면 안 된다.
 *
 * `route` 에는 `/center/students/[id]` 같은 패턴을 넣는다(Provider 기본 이벤트는
 * 실제 경로). 대시보드는 이 값으로 화면 단위 집계를 한다.
 */
export function emitPlaceTelemetryEvent(
  eventType: 'custom' | 'error',
  attributes: TelemetryAttributes,
  options: EmitPlaceTelemetryOptions = {},
): void {
  if (typeof window === 'undefined') return

  try {
    // 토큰 발급을 기다리는 사이 경로·사용자가 바뀌어도 발생 시점 값으로 보낸다.
    const pathname = options.pathname ?? window.location.pathname
    const occurredAt = new Date().toISOString()
    const userId = telemetryUserId
    const sessionId = getOrCreateTelemetrySessionId()
    const anonymousId = getOrCreateTelemetryAnonymousId()

    getOrFetchTelemetryAnonymousToken({ serviceId: PLACE_TELEMETRY_SERVICE_ID, anonymousId, sessionId })
      .then((auth) => {
        const serviceId = auth?.serviceId || PLACE_TELEMETRY_SERVICE_ID
        if (!auth || !serviceId) return

        const event = createTelemetryEvent({
          eventType,
          serviceId,
          serviceName: PLACE_TELEMETRY_SERVICE_NAME,
          sessionId,
          anonymousId,
          deviceFingerprint: getDeviceFingerprint(),
          userId,
          deploymentEnvironment: telemetryDeploymentEnvironment,
          page: pathname,
          route: toRoutePattern(pathname),
          url: `${window.location.origin}${pathname}`,
          pathname,
          referrer: document.referrer,
          occurredAt,
          environment: collectTelemetryEnvironment({ runtime: 'next-app-router', ...PLACE_TELEMETRY_ENVIRONMENT }),
          attributes,
        })
        if (event) sendTelemetryEvent({ token: auth.token, event })
      })
      .catch(() => {})
  } catch {
    // 저장소 차단(시크릿 모드) 등 — 보내지 않고 넘어간다.
  }
}

export type PlaceTelemetryErrorSource = 'window' | 'unhandledrejection' | 'react' | 'query' | 'mutation'

export interface ReportPlaceTelemetryErrorOptions {
  source: PlaceTelemetryErrorSource
  /** 앱이 받아서 처리한 에러(쿼리/뮤테이션 실패)인지, 아무도 못 잡은 에러인지 */
  handled: boolean
  /** 쿼리 키 첫 원소 — 어느 기능에서 실패했는지 */
  op?: string | null
  /** Next.js 서버 에러 digest — Vercel 로그의 같은 에러를 찾는 열쇠 */
  digest?: string | null
  /** 스택이 없을 때의 위치 (`file:line:col`) */
  sourceRef?: string | null
}

const errorThrottle = new TelemetryErrorThrottle()

export function reportPlaceTelemetryError(error: unknown, options: ReportPlaceTelemetryErrorOptions): void {
  try {
    const captured = toCapturedError(error)
    if (!captured || isIgnorableTelemetryError(captured)) return
    if (!errorThrottle.shouldSend(captured)) return

    const sourceRef = options.sourceRef ? normalizeStackFrame(options.sourceRef) : null

    emitPlaceTelemetryEvent('error', {
      errorKind: captured.errorKind,
      errorLabel: captured.errorLabel,
      // OS 는 stackTop 이 없을 때만 sourceRef 로 대체하므로 빈 문자열은 보내지 않는다.
      ...(captured.stackTop ? { stackTop: captured.stackTop } : {}),
      ...(sourceRef ? { sourceRef } : {}),
      handled: options.handled,
      errorSource: options.source,
      ...(captured.errorCode ? { errorCode: captured.errorCode } : {}),
      ...(options.op ? { op: options.op } : {}),
      // Next digest 는 숫자 문자열이라 sanitizer 의 전화번호 패턴에 걸려 가려진다(OS 는 아예 거절).
      // 앞에 `#` 를 붙여 피한다 — Vercel 로그에서는 숫자 부분으로 찾으면 된다.
      ...(options.digest ? { digest: `#${options.digest}` } : {}),
    })
  } catch {
    // 에러 보고가 또 다른 에러를 만들지 않는다.
  }
}

/**
 * 아무도 잡지 못한 런타임 에러(window error / unhandledrejection)를 보고한다.
 * 해제 함수를 돌려준다.
 */
export function installPlaceErrorCapture(): () => void {
  if (typeof window === 'undefined') return () => {}

  const handleError = (event: ErrorEvent): void => {
    reportPlaceTelemetryError(event.error ?? event.message, {
      source: 'window',
      handled: false,
      sourceRef: event.filename ? `${event.filename}:${event.lineno ?? 0}:${event.colno ?? 0}` : null,
    })
  }
  const handleRejection = (event: PromiseRejectionEvent): void => {
    reportPlaceTelemetryError(event.reason, { source: 'unhandledrejection', handled: false })
  }

  window.addEventListener('error', handleError)
  window.addEventListener('unhandledrejection', handleRejection)
  return () => {
    window.removeEventListener('error', handleError)
    window.removeEventListener('unhandledrejection', handleRejection)
  }
}
